/*
 * header-shell.js — <arctic-shell> custom element
 *
 * Wraps the new header chrome (header-v2 ship 1). Owns:
 *
 *   1. `--header-height` CSS custom property, published to :root via a
 *      single ResizeObserver. Other theme components consume it.
 *
 *   2. Compact sticky reveal. Mirrors Dawn's scroll-up reveal behavior with an
 *      Arctic-owned state on the middle header row only. The real utility/nav
 *      rows stay in document flow so mega menu and borders keep their normal
 *      contracts.
 *
 *   3. Shared overlay backdrop + scroll-lock. Listens for OVERLAY_OPEN /
 *      OVERLAY_CLOSE events bubbled from any descendant (mega menu, mobile
 *      drawer, header search). Toggles `.arctic-shell__backdrop[data-active]`.
 *
 * Re-uses the same arctic-header-events.js module the rest of the theme uses,
 * Listen on `document` so all header-adjacent overlays share one backdrop even
 * when an implementation detail renders outside this element later.
 */

import {
  OVERLAY_OPEN,
  OVERLAY_CLOSE,
  lockScroll,
  unlockScroll,
} from './arctic-header-events.js';

class ArcticShell extends HTMLElement {
  constructor() {
    super();
    this._activeOverlays = new Map();
    this.currentScrollTop = 0;
    this.headerBottomThreshold = 0;
    this.compactResetOffset = 0;
  }

  connectedCallback() {
    this.backdrop = this.querySelector('.arctic-shell__backdrop');

    this._resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry || !entry.borderBoxSize?.[0]) return;
      const height = Math.round(entry.borderBoxSize[0].blockSize);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      this._measureStickyRows();
    });
    this._resizeObserver.observe(this);

    document.addEventListener(OVERLAY_OPEN, this._onOverlayOpen);
    document.addEventListener(OVERLAY_CLOSE, this._onOverlayClose);
    this.backdrop?.addEventListener('pointerdown', this._onBackdropPointerDown);

    this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
    this._measureStickyRows();
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener(OVERLAY_OPEN, this._onOverlayOpen);
    document.removeEventListener(OVERLAY_CLOSE, this._onOverlayClose);
    this.backdrop?.removeEventListener('pointerdown', this._onBackdropPointerDown);
    this._activeOverlays.forEach((entry, targetEl) => {
      if (entry.scrollLock) unlockScroll(targetEl);
    });
    this._activeOverlays.clear();
    document.documentElement.style.removeProperty('--header-height');
    document.documentElement.style.removeProperty('--header-compact-height');
  }

  reveal() {
    if ((window.pageYOffset || document.documentElement.scrollTop) <= 0) {
      this.reset();
      return;
    }

    this._setCompactOffset(window.pageYOffset || document.documentElement.scrollTop);
    this.classList.add('is-compact-header-visible');
  }

  hide() {
    this.classList.remove('is-compact-header-visible');
    this._closeActiveOverlays();
  }

  reset() {
    this.classList.remove('is-compact-header-visible');
    this.style.removeProperty('--header-compact-offset');
  }

  _measureStickyRows() {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const utilityRow = this.querySelector(isDesktop ? '.header-shell__utility' : '.header-shell__mobile-utility');
    const compactRow = this.querySelector(isDesktop ? '.header-shell__main' : '.header-shell__mobile-main');
    const navRow = this.querySelector('.header-shell__nav');
    const mobileSearchRow = this.querySelector('.header-shell__mobile-search');

    const utilityHeight = utilityRow instanceof HTMLElement ? utilityRow.offsetHeight : 0;
    const compactHeight = compactRow instanceof HTMLElement ? compactRow.offsetHeight : 0;
    const navHeight = isDesktop && navRow instanceof HTMLElement ? navRow.offsetHeight : 0;
    const mobileSearchHeight = !isDesktop && mobileSearchRow instanceof HTMLElement ? mobileSearchRow.offsetHeight : 0;

    this.compactResetOffset = utilityHeight;
    this.headerBottomThreshold = utilityHeight + compactHeight + navHeight + mobileSearchHeight;

    if (compactHeight > 0) {
      document.documentElement.style.setProperty('--header-compact-height', `${compactHeight}px`);
    }
  }

  _onResize = () => {
    this._measureStickyRows();
    this._setCompactOffset(window.pageYOffset || document.documentElement.scrollTop);
  }

  _setCompactOffset(scrollTop) {
    const offset = Math.max(0, this.compactResetOffset - scrollTop);
    this.style.setProperty('--header-compact-offset', `${offset}px`);
  }

  _onOverlayOpen = (event) => {
    const targetEl = event.detail?.el;
    if (!(targetEl instanceof HTMLElement)) return;

    const entry = {
      source: event.detail?.source || 'unknown',
      scrollLock: event.detail?.scrollLock !== false,
    };
    const previousEntry = this._activeOverlays.get(targetEl);

    this._activeOverlays.set(targetEl, entry);
    if (this.backdrop) this.backdrop.setAttribute('data-active', '');
    if (entry.scrollLock && !previousEntry?.scrollLock) lockScroll(targetEl);
  };

  _onOverlayClose = (event) => {
    const targetEl = event.detail?.el;
    if (!(targetEl instanceof HTMLElement)) return;

    const entry = this._activeOverlays.get(targetEl);
    this._activeOverlays.delete(targetEl);
    if (entry?.scrollLock) unlockScroll(targetEl);
    if (this._activeOverlays.size === 0 && this.backdrop) {
      this.backdrop.removeAttribute('data-active');
    }
  };

  _onScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const headerBottom = this.headerBottomThreshold || this.offsetHeight;

    if (scrollTop < 0) return;

    if (this._activeOverlays.size > 0) {
      this.currentScrollTop = scrollTop;
      return;
    }

    this._setCompactOffset(scrollTop);

    if (scrollTop > this.currentScrollTop && scrollTop > headerBottom) {
      requestAnimationFrame(() => this.hide());
    } else if (scrollTop < this.currentScrollTop && scrollTop > headerBottom) {
      requestAnimationFrame(() => this.reveal());
    } else if (scrollTop <= 0) {
      requestAnimationFrame(() => this.reset());
    }

    this.currentScrollTop = scrollTop;
  };

  _onBackdropPointerDown = (event) => {
    if (event.button && event.button !== 0) return;
    event.preventDefault();

    Array.from(this._activeOverlays.entries())
      .reverse()
      .forEach(([targetEl, entry]) => this._requestOverlayClose(targetEl, entry));
  };

  _requestOverlayClose(targetEl, entry) {
    if (entry.source === 'header-search') {
      targetEl.closest('arctic-header-search')?.close?.();
      return;
    }

    if (entry.source === 'mega-menu') {
      targetEl.closest('arctic-mega-menu')?.close?.();
      return;
    }

    if (entry.source === 'mobile-menu') {
      targetEl.closest('arctic-mobile-menu')?.close?.({ restoreFocus: false });
    }
  }

  _closeActiveOverlays() {
    Array.from(this._activeOverlays.entries())
      .reverse()
      .forEach(([targetEl, entry]) => this._requestOverlayClose(targetEl, entry));
  }
}

if (!customElements.get('arctic-shell')) {
  customElements.define('arctic-shell', ArcticShell);
}
